import React, { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  Button,
  Card,
  Collapse,
  Form,
  InputNumber,
  Space,
  Typography,
} from 'antd';
// API
import { GET_INTERVIEW_SETTINGS } from 'api/interview/queries';
import { SET_INTERVIEW_SETTINGS } from 'api/interview/mutations';
// UI
import { errorNotification, successNotification } from 'ui/Notification';

const { Text } = Typography;
const { Panel } = Collapse;

const DISPATCH_ID = 'InterviewNowDispatchSettings';

// Entity defaults — mirror the backend's @ChildEntity field defaults so the form
// shows sensible values even when no row exists yet.
// At default 50 / 5min, ~5000 athletes complete in ~8.25h.
const DEFAULT_BATCH_SIZE = 50;
const DEFAULT_BATCH_DELAY_MINUTES = 5;

const MAX_BATCH_SIZE = 5000; // matches backend @Max(5000)
const MAX_BATCH_DELAY_MINUTES = 10080; // matches backend @Max(10080) (1 week)

type DispatchSettingsRow = {
  id: string;
  config?: {
    batchSize?: number | null;
    batchDelayMinutes?: number | null;
  } | null;
};

type FormValues = {
  batchSize: number;
  batchDelayMinutes: number;
};

const DispatchSettingsCard: React.FC = () => {
  const [form] = Form.useForm<FormValues>();
  const [dirty, setDirty] = useState<boolean>(false);

  const { data, loading, refetch } = useQuery(GET_INTERVIEW_SETTINGS, {
    fetchPolicy: 'cache-and-network',
  });

  const [setInterviewSettings, { loading: saving }] = useMutation(
    SET_INTERVIEW_SETTINGS,
    {
      onCompleted: () => {
        successNotification('Dispatch settings saved');
        setDirty(false);
        refetch();
      },
      onError: (err) => {
        errorNotification(err.message);
      },
    }
  );

  const settings = (data?.getInterviewSettings || []) as DispatchSettingsRow[];
  const current = settings.find((s) => s.id === DISPATCH_ID);
  const currentBatchSize = current?.config?.batchSize ?? DEFAULT_BATCH_SIZE;
  const currentBatchDelayMinutes =
    current?.config?.batchDelayMinutes ?? DEFAULT_BATCH_DELAY_MINUTES;

  useEffect(() => {
    form.setFieldsValue({
      batchSize: currentBatchSize,
      batchDelayMinutes: currentBatchDelayMinutes,
    });
    setDirty(false);
  }, [currentBatchSize, currentBatchDelayMinutes, form]);

  const handleSave = async (): Promise<void> => {
    const values = await form.validateFields();
    await setInterviewSettings({
      variables: {
        input: {
          InterviewNowDispatchSettings: {
            config: {
              batchSize: values.batchSize,
              batchDelayMinutes: values.batchDelayMinutes,
            },
          },
        },
      },
    });
  };

  return (
    <Card style={{ marginBottom: 16 }} bodyStyle={{ padding: 0 }}>
      <Collapse ghost>
        <Panel header="Dispatch settings" key="dispatch">
          <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
            Controls how staggered notification batches are sized and spaced.
            Batch 0 always fires immediately; subsequent batches are scheduled.
            Defaults (50 athletes / 5 min) complete ~5000 athletes in ~8.25h.
          </Text>
          <Form
            form={form}
            layout="vertical"
            initialValues={{
              batchSize: DEFAULT_BATCH_SIZE,
              batchDelayMinutes: DEFAULT_BATCH_DELAY_MINUTES,
            }}
            onValuesChange={() => setDirty(true)}
          >
            <Space size="large" align="start">
              <Form.Item
                name="batchSize"
                label="Batch size (athletes per batch)"
                rules={[
                  { required: true, message: 'Batch size is required' },
                  {
                    type: 'number',
                    min: 1,
                    max: MAX_BATCH_SIZE,
                    message: `Must be between 1 and ${MAX_BATCH_SIZE}`,
                  },
                ]}
              >
                <InputNumber
                  min={1}
                  max={MAX_BATCH_SIZE}
                  style={{ width: 220 }}
                />
              </Form.Item>
              <Form.Item
                name="batchDelayMinutes"
                label="Batch delay (minutes between batches)"
                rules={[
                  { required: true, message: 'Batch delay is required' },
                  {
                    type: 'number',
                    min: 1,
                    max: MAX_BATCH_DELAY_MINUTES,
                    message: `Must be between 1 and ${MAX_BATCH_DELAY_MINUTES}`,
                  },
                ]}
              >
                <InputNumber
                  min={1}
                  max={MAX_BATCH_DELAY_MINUTES}
                  style={{ width: 220 }}
                />
              </Form.Item>
            </Space>
            <Form.Item style={{ marginBottom: 0 }}>
              <Button
                type="primary"
                onClick={handleSave}
                loading={saving || loading}
                disabled={!dirty}
              >
                Save
              </Button>
            </Form.Item>
          </Form>
        </Panel>
      </Collapse>
    </Card>
  );
};

export default DispatchSettingsCard;
